import { httpClient, toClientApiError } from "./httpClient";

async function wrap<T>(fn: () => Promise<T>): Promise<T> {
  try {
    return await fn();
  } catch (error) {
    throw toClientApiError(error);
  }
}

/* ── Smart data entry «הזנה חכמה» (ADR-0035 → ADR-0037) ── */
export type ColumnRole =
  | "date"
  | "amount"
  | "credit"
  | "debit"
  | "type"
  | "description"
  | "name"
  | "phone"
  | "email"
  | "id_number"
  | "customer_code"
  | "category"
  | "payment_method"
  | "reference"
  | "notes"
  | "product"
  | "rep"
  | "fund"
  | "currency"
  | "exchange_rate"
  | "installments"
  | "address"
  | "city"
  | "contact_name"
  | "ignore";

export type ImportType = "auto" | "bank" | "payments" | "incomes" | "expenses" | "sales" | "customers" | "suppliers";
/** a file that is a list of records (the entity sub-flow), not money movements */
export type EntityTarget = "customers" | "suppliers";
export type Currency = "ILS" | "USD" | "EUR" | "GBP";
export type BankLineDto = { id: string; date: string; amount: number; description: string };
export type RowKind = "payment" | "income" | "expense" | "sale" | "skip";
export type PaymentMethod = "bank_transfer" | "cash" | "check" | "credit_card" | "app" | "standing_order" | "other";
export type CounterpartyType = "customer" | "supplier";
export type MatchType = "phone" | "id_number" | "email" | "code" | "name" | "manual" | "created" | "chat";

export type ColumnGuessDto = { header: string; role: ColumnRole; confidence: number; source: "heuristic" | "ai" | "user"; samples: string[] };

export type CounterpartyDto = {
  type: CounterpartyType;
  id: string;
  label: string;
  sub: string | null;
  score: number;
  matchType: MatchType;
  confirmed: boolean;
  ambiguous: boolean;
};
export type RefDto = { id: string; name: string; source: string };
export type NewEntityDto = { name: string; phone: string | null; email: string | null; idNumber: string | null };
export type OpenDealDto = { id: string; label: string; balanceAgorot: number; dealOn: string };
export type DuplicateDto = {
  inFile: boolean;
  strength: "strong" | "possible";
  label: string;
  amount: number | null;
  date: string | null;
  table: "payment" | "income" | "expense" | null;
  id: string | null;
};
export type ExistingDto = {
  table: "payment" | "income" | "expense";
  id: string;
  how: "reference";
  amount: number;
  date: string;
  label: string;
  linked: { type: CounterpartyType; id: string; label: string } | null;
};

/** One file line as the server proposes it (amounts in ₪). */
export type DataEntryRowDto = {
  index: number;
  line: number;
  raw: Array<{ header: string; value: string; role: ColumnRole }>;
  source: string;
  date: string | null;
  amount: number | null;
  direction: "in" | "out" | null;
  kind: RowKind;
  kindConfidence: number;
  kindReason: string;
  counterparty: CounterpartyDto | null;
  candidates: CounterpartyDto[];
  newEntity: NewEntityDto | null;
  category: RefDto | null;
  fund: RefDto | null;
  paymentMethod: PaymentMethod | null;
  paymentSource: string | null;
  description: string;
  reference: string | null;
  notes: string | null;
  rep: { id: string; name: string } | null;
  product: RefDto | null;
  deals: OpenDealDto[];
  useDeal: boolean;
  salePaid: boolean;
  recurrence: { label: string; groupId: string } | null;
  duplicate: DuplicateDto | null;
  duplicateAction: "skip" | "create" | null;
  lastSeen: { amount: number; date: string } | null;
  existing: ExistingDto | null;
  existingAction: "keep" | "create" | null;
  /** the amount is in this currency (older sessions: missing = ILS) */
  currency?: Currency;
  exchangeRate?: number | null;
  installments: number | null;
  /** an open line of the ledger's bank statement with the same amount — matched on commit when useBank */
  bankLine?: BankLineDto | null;
  useBank?: boolean;
  ruleApplied: string | null;
  flags: string[];
  status: "ready" | "review";
  ai: { kind?: RowKind; why?: string; confidence?: number } | null;
};

/** what the agent proposes again for one row when its kind / counterparty changes */
export type SuggestionDto = Pick<
  DataEntryRowDto,
  "counterparty" | "candidates" | "newEntity" | "category" | "fund" | "paymentMethod" | "paymentSource" | "description" | "lastSeen" | "deals" | "useDeal" | "product" | "rep"
>;

export type FactDto = { key: string; label: string; value: number | string; kind?: "money" | "range"; tone?: "success" | "danger" | "warning"; flag?: string };
export type WarningDto = { flag: string | null; level: "danger" | "warning" | "info" | "success" | "neutral"; count: number; text: string };
export type InsightsDto = {
  profile: { key: string; label: string; text: string };
  facts: FactDto[];
  warnings: WarningDto[];
  recommendations: string[];
  narrative: { summary: string; recommendations: string[] } | null;
};

export type RuleWhen = { field: "category" | "type" | "name" | "description" | "counterparty"; op: "equals" | "contains"; value: string };
export type RuleThen = { kind?: RowKind; categoryId?: string; fundId?: string };
export type AppliedRuleDto = {
  id: string | null;
  when: RuleWhen;
  then: RuleThen & { categoryName?: string; fundName?: string };
  saved: boolean;
  ledgerId: string | null;
  label: string;
  hits?: number;
};
export type SuggestedRuleDto = { when: RuleWhen; then: { kind: RowKind }; rows: number; agreement: number; label: string };
export type SavedRuleDto = { id: string; label: string; ledgerId: string | null; rule: unknown; lastUsedAt: string | null };

export type RecurrenceGroupDto = {
  id: string;
  label: string;
  kind: RowKind;
  amount: number;
  count: number;
  firstDate: string;
  lastDate: string;
  frequencyLabel: string;
  rowIndexes: number[];
};

export type CommitResultDto = {
  index: number;
  ok: boolean;
  kind?: RowKind;
  id?: string;
  skipped?: boolean;
  kept?: boolean;
  deal?: boolean;
  /** a split row: how many records it wrote */
  parts?: number;
  /** the bank statement match — the record is written even when matching failed */
  bank?: { matched: boolean; error?: string };
  error?: string;
};

export type ChatActionDto =
  | { type: "approve_rows" | "show_rows" | "open_row"; label: string; indexes?: number[]; flag?: string }
  | { type: "approve_ready"; label: string }
  | { type: "open_tab"; label: string; tab: "rows" | "table" | "analysis" | "summary" }
  | { type: "columns"; columns: Array<{ header: string; role: ColumnRole }> }
  | { type: "rule_saved"; rule: { id: string; label: string } };
export type ChatItemDto = { role: "user" | "assistant"; text: string; at: string; steps?: string[]; actions?: ChatActionDto[]; changed?: number };
export type RowPatchDto = Partial<DataEntryRowDto>;

export type DataEntryLookupsDto = {
  categories: Array<{ id: string; name: string; parentId: string | null }>;
  funds: Array<{ id: string; name: string; isDefault: boolean }>;
  reps: Array<{ id: string; name: string }>;
  products: Array<{ id: string; name: string; priceAgorot: number }>;
  kinds: RowKind[];
  paymentMethods: Array<{ value: PaymentMethod; label: string }>;
  currencies?: Currency[];
  /** bank reconciliation is on and the user may match lines (needs a ledger) */
  bankOn?: boolean;
  openBankLines?: number;
  /** the record lists this user may load */
  entityTargets?: EntityTarget[];
};

export type DataEntryDefaults = { categoryId: string | null; fundId: string | null; paymentMethod: PaymentMethod | null };

export type DataEntrySessionDto = {
  id: string;
  fileName: string;
  status: "open" | "done" | string;
  sheets: Array<{ index: number; name: string; rows: number }>;
  sheet: number;
  sheetName: string;
  headers: string[];
  columns: ColumnGuessDto[];
  roles: Record<ColumnRole, string>;
  sample: string[][];
  totalRows: number;
  importType: ImportType;
  suggestedType: ImportType;
  isBank: boolean;
  ledger: { id: string; name: string } | null;
  defaults: DataEntryDefaults;
  analyzed: boolean;
  rows: DataEntryRowDto[];
  groups: RecurrenceGroupDto[];
  insights: InsightsDto | null;
  rules: AppliedRuleDto[];
  suggestedRules: SuggestedRuleDto[];
  savedRules: SavedRuleDto[];
  lookups: DataEntryLookupsDto;
  ai: { enabled: boolean; used: boolean; error: string | null; capped: boolean; note: string | null };
  committed: CommitResultDto[];
  chat: ChatItemDto[];
  /** the file was loaded into the ledger's bank statement (DataEntryBankLoad) */
  bank?: { status: "loaded"; ledgerId: string; loaded: number; at: string } | null;
  /** recognized as / saved as a recurring upload */
  recurring?: { id: string; name: string; frequencyLabel: string; nextDueDate: string; applied: boolean } | null;
  /** a list of records that was scanned in the entity flow */
  entityTarget?: EntityTarget | null;
  createdAt: string;
  updatedAt: string;
};

/* ── phase 3: bank statement load ── */
export type BankLoadStatsDto = {
  totalRows: number;
  incomeCount: number;
  expenseCount: number;
  newCount: number;
  alreadyInBank: number;
  invalid: number;
  foreign: number;
  periodFrom: string | null;
  periodTo: string | null;
};

/* ── phase 4: recurring uploads ── */
export type RecurringFrequency = "weekly" | "monthly" | "bi_monthly" | "quarterly" | "yearly" | "custom";
export type RecurringDto = {
  id: string;
  name: string;
  frequency: RecurringFrequency;
  frequencyLabel: string;
  customIntervalDays: number | null;
  nextDueDate: string;
  overdue: boolean;
  importType: ImportType;
  ledgerId: string | null;
  headers: string[];
  lastReceivedAt: string | null;
  lastSessionId: string | null;
  createdAt: string;
};

/* ── phase 3: a list of customers / suppliers ── */
export type EntityField = "name" | "phone" | "email" | "idNumber" | "code" | "address" | "contactName" | "notes";
export type EntityValues = Partial<Record<EntityField, string | null>>;
export type EntityAction = "create" | "skip" | "merge_empty_fields" | "update_existing";
export type EntityChangeDto = { key: EntityField; label: string; from: string | null; to: string };
export type EntityEntryDto = {
  index: number;
  line: number;
  raw: Array<{ header: string; value: string; role: ColumnRole }>;
  values: EntityValues;
  fields: Array<{ key: EntityField; label: string; value: string }>;
  status: "ready" | "review" | "duplicate" | "error";
  errors: string[];
  warnings: string[];
  existing: { id: string | null; label: string; sub: string | null; matchType: string; weak: boolean; inFile: boolean; line: number | null } | null;
  duplicateAction: EntityAction;
  actions: EntityAction[];
  merge: EntityChangeDto[];
  update: EntityChangeDto[];
};
export type EntityCommittedDto = { index: number; action: "created" | "updated"; id: string; label: string };
export type EntityAnalysisDto = {
  target: EntityTarget;
  label: string;
  fields: Array<{ key: EntityField; label: string }>;
  entries: EntityEntryDto[];
  committed: EntityCommittedDto[];
  can: { create: boolean; update: boolean };
  columns: ColumnGuessDto[];
};
export type EntityCommitResultDto = {
  index: number;
  ok: boolean;
  action?: "created" | "updated";
  id?: string;
  label?: string;
  skipped?: boolean;
  note?: string;
  error?: string;
};

export type DataEntrySessionSummaryDto = {
  id: string;
  fileName: string;
  status: string;
  importType: ImportType;
  rowCount: number;
  committed: number;
  analyzed: boolean;
  updatedAt: string;
};

export type SessionOptionsBody = {
  importType?: ImportType;
  ledgerId?: string | null;
  columns?: Array<{ header: string; role: ColumnRole }>;
  defaults?: Partial<DataEntryDefaults>;
  rules?: Array<{ when: RuleWhen; then: RuleThen }>;
  useAi?: boolean;
};

/** One approved row, with every value as it is on screen (Turbo's commitPayload). */
export type CommitRowBody = {
  index: number;
  kind: RowKind;
  date: string | null;
  amount: number | null;
  paymentMethod: PaymentMethod | null;
  counterparty: { type: CounterpartyType; id: string; label: string } | null;
  newName: string | null;
  categoryId: string | null;
  fundId: string | null;
  description: string | null;
  notes: string | null;
  reference: string | null;
  repId: string | null;
  productId: string | null;
  dealId: string | null;
  salePaid: boolean;
  duplicateAction: "skip" | "create" | null;
  existingAction: "keep" | "create" | null;
  currency: Currency;
  exchangeRate: number | null;
  installments: number | null;
  /** income / expense split into categories (amounts in the row's currency) */
  categorySplits?: Array<{ categoryId: string | null; amount: number }>;
  bankLineId: string | null;
};

export type RowsStateItem = {
  i: number;
  kind: RowKind;
  cat: string | null;
  catName: string | null;
  cp: [CounterpartyType, string, string] | null;
  pm: string | null;
  desc: string | null;
  st: string;
  ea: string | null;
  da: string | null;
};

export type SimilarGroupDto = { key: string; by: RuleWhen["field"]; value: string; label: string; count: number; indexes: number[] };
export type LearnChange = { kind?: RowKind; categoryId?: string; fundId?: string };

const BASE = "/ai/data-entry";
const LONG = { timeout: 180_000 };

const post = <T,>(url: string, body: unknown, opts?: { timeout?: number }) =>
  wrap(async () => (await httpClient.post<{ item: T }>(url, body, opts)).data.item);

export const aiDataEntryApi = {
  upload(file: File): Promise<DataEntrySessionDto> {
    return wrap(async () => {
      const form = new FormData();
      form.append("file", file);
      const res = await httpClient.post<{ item: DataEntrySessionDto }>(`${BASE}/upload`, form, {
        timeout: 120_000,
        headers: { "Content-Type": undefined },
        transformRequest: [
          (data, headers) => {
            // let the browser set the multipart boundary
            if (headers && typeof headers === "object" && "delete" in headers) {
              (headers as { delete: (key: string) => void }).delete("Content-Type");
            } else if (headers && typeof headers === "object") {
              delete (headers as Record<string, unknown>)["Content-Type"];
              delete (headers as Record<string, unknown>)["content-type"];
            }
            return data;
          },
        ],
      });
      return res.data.item;
    });
  },
  sessions(): Promise<DataEntrySessionSummaryDto[]> {
    return wrap(async () => (await httpClient.get<{ items: DataEntrySessionSummaryDto[] }>(`${BASE}/sessions`)).data.items);
  },
  get(id: string): Promise<DataEntrySessionDto> {
    return wrap(async () => (await httpClient.get<{ item: DataEntrySessionDto }>(`${BASE}/${id}`)).data.item);
  },
  remove(id: string): Promise<void> {
    return wrap(async () => {
      await httpClient.delete(`${BASE}/${id}`);
    });
  },
  selectSheet(id: string, sheet: number): Promise<DataEntrySessionDto> {
    return post(`${BASE}/${id}/sheet`, { sheet });
  },
  setOptions(id: string, body: SessionOptionsBody): Promise<DataEntrySessionDto> {
    return post(`${BASE}/${id}/options`, body);
  },
  analyze(id: string, body: SessionOptionsBody): Promise<DataEntrySessionDto> {
    return post(`${BASE}/${id}/analyze`, body, LONG);
  },
  suggest(id: string, body: { index: number; kind: RowKind; counterparty?: { type: CounterpartyType; id: string } | null }): Promise<SuggestionDto> {
    return post(`${BASE}/${id}/suggest`, body);
  },
  similar(id: string, body: { index: number; change: LearnChange; excludeIndexes?: number[] }): Promise<{ groups: SimilarGroupDto[] }> {
    return post(`${BASE}/${id}/similar`, body);
  },
  propagate(
    id: string,
    body: { index: number; change: LearnChange; groupKey: string; saveRule: boolean; excludeIndexes?: number[] },
  ): Promise<{ patches: Array<{ index: number; patch: RowPatchDto }>; rule: SavedRuleDto | null }> {
    return post(`${BASE}/${id}/propagate`, body);
  },
  search(id: string, type: CounterpartyType, q: string): Promise<CounterpartyDto[]> {
    return wrap(async () => (await httpClient.get<{ item: { items: CounterpartyDto[] } }>(`${BASE}/${id}/search`, { params: { type, q } })).data.item.items);
  },
  createCounterparty(id: string, body: { type: CounterpartyType; name: string; phone?: string | null; email?: string | null; idNumber?: string | null }): Promise<CounterpartyDto> {
    return wrap(async () => (await httpClient.post<{ item: { counterparty: CounterpartyDto } }>(`${BASE}/${id}/counterparty`, body)).data.item.counterparty);
  },
  commit(id: string, rows: CommitRowBody[]): Promise<{ results: CommitResultDto[] }> {
    return post(`${BASE}/${id}/commit`, { rows }, LONG);
  },
  undo(id: string, index: number): Promise<{ ok: boolean }> {
    return post(`${BASE}/${id}/undo/${index}`, {});
  },
  chat(id: string, body: { message: string; rowsState: RowsStateItem[] }): Promise<{ reply: string; steps: string[]; patches: Array<{ index: number; patch: RowPatchDto }>; actions: ChatActionDto[] }> {
    return post(`${BASE}/${id}/chat`, body, LONG);
  },
  clearChat(id: string): Promise<void> {
    return wrap(async () => {
      await httpClient.delete(`${BASE}/${id}/chat`);
    });
  },
  listRules(ledgerId?: string | null): Promise<SavedRuleDto[]> {
    return wrap(async () => (await httpClient.get<{ items: SavedRuleDto[] }>(`${BASE}/rules`, { params: ledgerId ? { ledgerId } : {} })).data.items);
  },
  saveRule(body: { when: RuleWhen; then: RuleThen; ledgerId?: string | null }): Promise<SavedRuleDto> {
    return post(`${BASE}/rules`, body);
  },
  deleteRule(id: string): Promise<void> {
    return wrap(async () => {
      await httpClient.delete(`${BASE}/rules/${id}`);
    });
  },

  /* phase 3: the file into the ledger's bank statement (dryRun = the counts only) */
  bankLoad(id: string, body: { dryRun?: boolean; columns?: Array<{ header: string; role: ColumnRole }> }): Promise<{ stats: BankLoadStatsDto; loaded: number | null }> {
    return post(`${BASE}/${id}/bank-load`, body, LONG);
  },

  /* phase 4: recurring uploads */
  listRecurring(): Promise<RecurringDto[]> {
    return wrap(async () => (await httpClient.get<{ items: RecurringDto[] }>(`${BASE}/recurring`)).data.items);
  },
  saveRecurring(
    id: string,
    body: {
      name: string;
      frequency: RecurringFrequency;
      customIntervalDays?: number | null;
      nextDueDate: string;
      importType?: ImportType;
      columns?: Array<{ header: string; role: ColumnRole }>;
    },
  ): Promise<RecurringDto> {
    return post(`${BASE}/${id}/recurring`, body);
  },
  deleteRecurring(recurringId: string): Promise<void> {
    return wrap(async () => {
      await httpClient.delete(`${BASE}/recurring/${recurringId}`);
    });
  },

  /* phase 3: a list of customers / suppliers */
  entityAnalyze(id: string, body: { target: EntityTarget; columns?: Array<{ header: string; role: ColumnRole }> }): Promise<EntityAnalysisDto> {
    return post(`${BASE}/${id}/entity/analyze`, body, LONG);
  },
  entityCheck(id: string, body: { index: number; values: EntityValues; duplicateAction?: EntityAction | null }): Promise<EntityEntryDto> {
    return post(`${BASE}/${id}/entity/check`, body);
  },
  entityCommit(id: string, rows: Array<{ index: number; values?: EntityValues; duplicateAction?: EntityAction | null }>): Promise<{ results: EntityCommitResultDto[] }> {
    return post(`${BASE}/${id}/entity/commit`, { rows }, LONG);
  },
  entityUndo(id: string, index: number): Promise<{ ok: boolean }> {
    return post(`${BASE}/${id}/entity/undo/${index}`, {});
  },
};
